"use client";
import React from "react";
import { AbsoluteFill, Sequence, useCurrentFrame, useVideoConfig, interpolate } from "remotion";
import { loadFont } from "@remotion/google-fonts/NotoSansDevanagari";
import { HOLES_DATA_URI } from "./trimbakHolesData";
import { HolesLayer } from "./overlayKit/HolesLayer";
import { DevaKathaHypeCta, ctaBeatsFromWords } from "./DevaKathaHypeCta";

const { fontFamily } = loadFont("normal", { weights: ["400", "700", "800", "900"], subsets: ["devanagari", "latin"] });

// Khade Hanuman, Ujjain (911f84b2) — Trimbakeshwar holes treatment.
// H3 lane, Higgsfield 4K upscale → 30 fps parts → factory_4k_parts route. Comp 1280x720 @ 30 fps, scale=3 → 3840x2160.
// Every `start` RESOLVED from word_timestamps_hindi (3294 words, ends 1239.69s vs
// duration 1239.70s, not drifted). Generated by gen_khade_hanuman_overlays.py. 60 cards = 1 per 20.7s.
// Brief: short and crisp, <=4 tokens per card (one verbatim chaupai line at 5).
const PAD = 25;
const YELLOW = "#FFD60A";
const WHITE = "#FFFFFF";
const RED = "#FF3B2E";
const SH = "0 5px 14px rgba(0,0,0,0.9), 0 2px 5px rgba(0,0,0,0.95)";

const BASE_FS = 40, BIG_FS = 56, SMALL_FS = 28;   // Yash 2026-08-04 LAW: 40/56/28.
const STAGGER = 0.16, DROP = 26, DUR = 0.32;
const LH = 1.18;
const LINE_GAP = -8;

const CTA_TITLE = "उज्जैन के खड़े हनुमान, जो हिले नहीं";
const CTA_HANDLE = "@DevaKatha";
const CTA_COMMENT_TEXT = "जय बजरंगबली 🙏 सती गेट पर रोज़ माथा टेकता हूँ। ये देखकर आँखें भर आईं।";
// ⛔ https R2, never staticFile() — factory_4k_full pushes only src/, public/ is frozen in the image.
const CTA_SEG = (n: number) =>
  `https://pub-bb3ce5df1bc243da96f744a22c134897.r2.dev/overlay_assets/khade_hanuman_ujjain_cta${n}_seg.mp4`;

type Word = { t: string; c?: "y" | "w" | "r"; big?: boolean; small?: boolean };
export type Phrase = { lines: Word[][]; start: number; out: number; lineGap?: number; lh?: number; ls?: string };
const col = (c?: string) => (c === "w" ? WHITE : c === "r" ? RED : YELLOW);

export const KHADE_HANUMAN_PHRASES: Phrase[] = [
  { start: 6.82, out: 10.22, lines: [[{ t: "आठ", c: "y", big: true }, { t: "फुट", c: "y", big: true }]] },   // आठ
  { start: 23.81, out: 27.81, lines: [[{ t: "वही", c: "w", small: true }, { t: "जवान,", c: "y", big: true }], [{ t: "आज", c: "r", big: true }, { t: "प्रार्थना", c: "r", big: true }]] },   // जवान
  { start: 35.97, out: 39.37, lines: [[{ t: "हिलने", c: "r", big: true }, { t: "से", c: "w", small: true }, { t: "मना", c: "r", big: true }]] },   // हिलने
  { start: 52.87, out: 56.87, lines: [[{ t: "चार", c: "y", big: true }, { t: "दिन,", c: "y" }], [{ t: "चार", c: "r", big: true }, { t: "कोशिशें", c: "r", big: true }]] },   // चार
  { start: 62.55, out: 65.95, lines: [[{ t: "डॉक्टर", c: "r", big: true }, { t: "हनुमान?", c: "r", big: true }]] },   // डॉक्टर
  { start: 66.13, out: 71.13, lines: [[{ t: "170", c: "y", big: true }, { t: "साल", c: "y", big: true }], [{ t: "या", c: "w", small: true }, { t: "1,000?", c: "r", big: true }]] },   // सत्तर
  { start: 90.87, out: 94.27, lines: [[{ t: "20", c: "y", big: true }, { t: "अगस्त", c: "y", big: true }]] },   // अगस्त
  { start: 97.73, out: 101.73, lines: [[{ t: "मोहन", c: "y", big: true }, { t: "यादव,", c: "y", big: true }, { t: "पैदल", c: "r", big: true }]] },   // मोहन
  { start: 116.39, out: 119.79, lines: [[{ t: "सिंहस्थ", c: "r", big: true }, { t: "2028", c: "r", big: true }]] },   // सिंहस्थ
  { start: 143.66, out: 147.06, lines: [[{ t: "जगह", c: "w" }, { t: "तय:", c: "w" }], [{ t: "टंकी", c: "r", big: true }, { t: "चौक", c: "r", big: true }]] },   // टंकी
  { start: 159.65, out: 164.15, lines: [[{ t: "दक्षिणमुखी", c: "y", big: true }, { t: "हनुमान,", c: "y" }], [{ t: "उत्तरमुखी?", c: "r", big: true }]] },   // दक्षिणमुखी
  { start: 178.04, out: 181.44, lines: [[{ t: "30", c: "y", big: true }, { t: "अगस्त,", c: "y", big: true }, { t: "रविवार", c: "w" }]] },   // अगस्त
  { start: 192.32, out: 195.72, lines: [[{ t: "बीच", c: "y", big: true }, { t: "सड़क,", c: "y", big: true }], [{ t: "हनुमान", c: "r", big: true }, { t: "चालीसा", c: "r", big: true }]] },   // बीच
  { start: 209.81, out: 213.21, lines: [[{ t: "4", c: "y", big: true }, { t: "सितंबर,", c: "y", big: true }, { t: "शुक्रवार", c: "w" }]] },   // सितंबर
  { start: 221.00, out: 225.50, lines: [[{ t: "शिखर,", c: "r", big: true }, { t: "गुंबद,", c: "r", big: true }, { t: "दीवारें", c: "r", big: true }]] },   // शिखर
  { start: 238.04, out: 243.54, lines: [[{ t: "अकेले", c: "y", big: true }, { t: "खड़े,", c: "y", big: true }], [{ t: "बिना", c: "w", small: true }, { t: "छत", c: "r", big: true }]] },   // अकेले
  { start: 244.87, out: 248.27, lines: [[{ t: "आख़िरी", c: "r", big: true }, { t: "महाआरती", c: "r", big: true }]] },   // आख़िरी
  { start: 262.71, out: 267.71, lines: [[{ t: "पाँचवीं", c: "y", big: true }, { t: "पीढ़ी:", c: "y" }], [{ t: "महेश", c: "r", big: true }, { t: "बैरागी", c: "r", big: true }]] },   // पाँचवीं
  { start: 292.98, out: 296.38, lines: [[{ t: "5", c: "y", big: true }, { t: "सितंबर,", c: "y", big: true }, { t: "शनिवार", c: "w" }]] },   // सितंबर
  { start: 317.48, out: 320.88, lines: [[{ t: "प्रतिमा", c: "w" }], [{ t: "वहीं", c: "r", big: true }, { t: "की", c: "w", small: true }, { t: "वहीं", c: "r", big: true }]] },   // प्रतिमा
  { start: 334.41, out: 337.81, lines: [[{ t: "वजह", c: "y", big: true }, { t: "अलग,", c: "y" }], [{ t: "नतीजा", c: "r", big: true }, { t: "वही", c: "r", big: true }]] },   // वजह
  { start: 354.47, out: 357.87, lines: [[{ t: "पत्थर", c: "y", big: true }, { t: "में", c: "w", small: true }, { t: "फँसा", c: "r", big: true }, { t: "पत्थर", c: "r", big: true }]] },   // पत्थर
  { start: 370.21, out: 373.61, lines: [[{ t: "बिना", c: "w", small: true }, { t: "लोहे,", c: "y", big: true }], [{ t: "बिना", c: "w", small: true }, { t: "सीमेंट", c: "y", big: true }]] },   // लोहे
  { start: 386.96, out: 391.96, lines: [[{ t: "खंडित", c: "r", big: true }, { t: "प्रतिमा:", c: "r" }], [{ t: "पूजा", c: "y", big: true }, { t: "बंद", c: "y", big: true }]] },   // खंडित
  { start: 398.57, out: 401.97, lines: [[{ t: "पहले", c: "y", big: true }, { t: "लिखकर", c: "r", big: true }, { t: "दीजिए", c: "r", big: true }]] },   // लिखकर
  { start: 435.78, out: 440.78, lines: [[{ t: "तबीयत", c: "y", big: true }, { t: "बिगड़", c: "y", big: true }, { t: "गई", c: "w" }], [{ t: "अस्पताल", c: "r", big: true }]] },   // तबीयत
  { start: 444.45, out: 447.85, lines: [[{ t: "50", c: "y", big: true }, { t: "से", c: "w", small: true }, { t: "ज़्यादा", c: "y", big: true }, { t: "वानर", c: "r", big: true }]] },   // पचास
  { start: 467.00, out: 470.40, lines: [[{ t: "7", c: "y", big: true }, { t: "सितंबर:", c: "y" }], [{ t: "शाही", c: "r", big: true }, { t: "सवारी", c: "r", big: true }]] },   // शाही
  { start: 494.12, out: 497.52, lines: [[{ t: "क्रेनें", c: "r", big: true }, { t: "ख़ामोश", c: "r", big: true }]] },   // क्रेनें
  { start: 507.48, out: 512.48, lines: [[{ t: "IIT", c: "y", big: true }, { t: "इंदौर,", c: "y", big: true }, { t: "जाँच", c: "r", big: true }]] },   // इंदौर
  { start: 540.48, out: 545.48, lines: [[{ t: "टेगोर:", c: "w" }], [{ t: "जल्दबाज़ी", c: "r", big: true }, { t: "में", c: "w", small: true }, { t: "नहीं", c: "r", big: true }]] },   // टेगोर
  { start: 577.95, out: 581.35, lines: [[{ t: "देवता", c: "r", big: true }, { t: "अपनी", c: "w", small: true }, { t: "जगह", c: "r", big: true }]] },   // देवता
  { start: 605.64, out: 611.14, lines: [[{ t: "संत,", c: "y", big: true }, { t: "संगठन,", c: "y", big: true }, { t: "विपक्ष:", c: "y", big: true }], [{ t: "साथ", c: "r", big: true }]] },   // संगठन
  { start: 628.98, out: 632.38, lines: [[{ t: "125", c: "r", big: true }, { t: "घंटे", c: "r", big: true }]] },   // पच्चीस
  { start: 649.55, out: 652.95, lines: [[{ t: "आँसुओं", c: "w" }, { t: "की", c: "w", small: true }, { t: "जगह", c: "w" }], [{ t: "जयकारे", c: "r", big: true }]] },   // आँसुओं
  { start: 687.67, out: 691.07, lines: [[{ t: "अपने", c: "w", small: true }, { t: "ख़र्च", c: "y", big: true }, { t: "पर", c: "w", small: true }, { t: "CCTV", c: "r", big: true }]] },   // ख़र्च
  { start: 699.57, out: 704.57, lines: [[{ t: "रात", c: "y", big: true }, { t: "12", c: "y", big: true }, { t: "बजे", c: "y" }], [{ t: "फ़ोन", c: "r", big: true }]] },   // बारह
  { start: 718.97, out: 722.37, lines: [[{ t: "9", c: "y", big: true }, { t: "सितंबर,", c: "y", big: true }, { t: "बुधवार", c: "w" }]] },   // सितंबर
  { start: 728.27, out: 731.67, lines: [[{ t: "बगैर", c: "y", big: true }, { t: "सहमति,", c: "y", big: true }], [{ t: "नहीं", c: "r", big: true }, { t: "हटेगी", c: "r", big: true }]] },   // बगैर
  { start: 755.80, out: 759.20, lines: [[{ t: "कलेक्टर:", c: "w" }], [{ t: "कोई", c: "w", small: true }, { t: "प्रयास", c: "r", big: true }, { t: "नहीं", c: "r", big: true }]] },   // प्रयास
  { start: 767.69, out: 771.09, lines: [[{ t: "तीन", c: "r", big: true }, { t: "रास्ते", c: "r", big: true }]] },   // तीन
  { start: 802.05, out: 805.45, lines: [[{ t: "हटाने", c: "y", big: true }, { t: "आए,", c: "y" }], [{ t: "मनाने", c: "r", big: true }, { t: "लगे", c: "r", big: true }]] },   // हटाने
  { start: 814.71, out: 820.71, lines: [[{ t: "राम", c: "y", big: true }, { t: "दुआरे", c: "y", big: true }, { t: "तुम", c: "y" }, { t: "रखवारे", c: "y", big: true }]] },   // दुआरे
  { start: 832.02, out: 836.52, lines: [[{ t: "तिरपाल,", c: "y", big: true }], [{ t: "बिना", c: "w", small: true }, { t: "मंदिर", c: "r", big: true }]] },   // तिरपाल
  { start: 841.24, out: 844.64, lines: [[{ t: "27", c: "r", big: true }, { t: "मार्च", c: "r", big: true }, { t: "2028", c: "r", big: true }]] },   // मार्च
  { start: 863.99, out: 867.39, lines: [[{ t: "सती", c: "y", big: true }, { t: "गेट,", c: "y" }], [{ t: "बड़ा", c: "r", big: true }, { t: "सराफा", c: "r", big: true }]] },   // सती
  { start: 879.74, out: 884.24, lines: [[{ t: "पहले", c: "w", small: true }, { t: "चौखट,", c: "y", big: true }], [{ t: "फिर", c: "w", small: true }, { t: "तिजोरी", c: "r", big: true }]] },   // पहले
  { start: 892.90, out: 897.90, lines: [[{ t: "खड़े।", c: "y", big: true }, { t: "सीधे।", c: "y", big: true }], [{ t: "आठ", c: "r", big: true }, { t: "फुट", c: "r", big: true }]] },   // सीधे
  { start: 898.24, out: 901.64, lines: [[{ t: "दक्षिण:", c: "y" }], [{ t: "लंका", c: "r", big: true }, { t: "की", c: "w", small: true }, { t: "दिशा", c: "r", big: true }]] },   // दिशा
  { start: 928.45, out: 935.45, lines: [[{ t: "डॉक्टर", c: "y", big: true }, { t: "हनुमान,", c: "y", big: true }], [{ t: "एम्स", c: "r", big: true }]] },   // डॉक्टर
  { start: 943.02, out: 948.02, lines: [[{ t: "संकट", c: "y", big: true }, { t: "कटै", c: "y", big: true }], [{ t: "मिटै", c: "r", big: true }, { t: "सब", c: "r" }, { t: "पीरा", c: "r", big: true }]] },   // संकट
  { start: 985.15, out: 988.55, lines: [[{ t: "1857", c: "r", big: true }]] },   // सत्तावन
  { start: 1011.55, out: 1014.95, lines: [[{ t: "उसी", c: "w", small: true }, { t: "चबूतरे,", c: "r", big: true }], [{ t: "उसी", c: "w", small: true }, { t: "दिशा", c: "r", big: true }]] },   // चबूतरे
  { start: 1035.93, out: 1039.33, lines: [[{ t: "मालवा", c: "y", big: true }, { t: "का", c: "w", small: true }, { t: "बेसाल्ट", c: "r", big: true }]] },   // बेसाल्ट
  { start: 1043.45, out: 1048.95, lines: [[{ t: "परमार,", c: "y", big: true }, { t: "राजा", c: "r", big: true }, { t: "भोज", c: "r", big: true }]] },   // परमार
  { start: 1067.54, out: 1072.54, lines: [[{ t: "ताला", c: "r", big: true }, { t: "जो", c: "w", small: true }, { t: "खुले", c: "r", big: true }, { t: "नहीं", c: "r", big: true }]] },   // ताला
  { start: 1107.52, out: 1112.52, lines: [[{ t: "दो", c: "y", big: true }, { t: "कहानियाँ", c: "y", big: true }], [{ t: "काग़ज़,", c: "r", big: true }, { t: "पत्थर", c: "r", big: true }]] },   // कहानियाँ
  { start: 1138.83, out: 1144.83, lines: [[{ t: "निष्ठा:", c: "y", big: true }], [{ t: "पत्थर,", c: "r", big: true }, { t: "पुजारी", c: "r", big: true }]] },   // निष्ठा
  { start: 1174.49, out: 1177.89, lines: [[{ t: "सबसे", c: "w", small: true }, { t: "बड़ा", c: "y", big: true }, { t: "वज़न", c: "y" }], [{ t: "रिपोर्ट", c: "r", big: true }]] },   // वज़न
  { start: 1198.28, out: 1201.68, lines: [[{ t: "राजा", c: "y", big: true }], [{ t: "रात", c: "r", big: true }, { t: "नहीं", c: "r", big: true }, { t: "रुकता", c: "r", big: true }]] },   // राजा
];

const PhraseCard: React.FC<{ p: Phrase }> = ({ p }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const t = frame / fps;
  let k = 0;
  return (
    <AbsoluteFill style={{ padding: PAD, alignItems: "flex-start", justifyContent: "flex-start" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: p.lineGap ?? LINE_GAP }}>
        {p.lines.map((line, li) => (
          <div key={li} style={{ display: "flex", alignItems: "flex-end", gap: 16, lineHeight: p.lh ?? LH }}>
            {line.map((w, wi) => {
              const delay = k++ * STAGGER;
              const prog = interpolate(t, [delay, delay + DUR], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
              const fs = w.big ? BIG_FS : w.small ? SMALL_FS : BASE_FS;
              return (
                <span key={wi} style={{
                  fontFamily, fontWeight: w.big ? 900 : 800, fontSize: fs, color: col(w.c),
                  textShadow: SH, letterSpacing: p.ls,
                  transform: `translateY(${(1 - prog) * -DROP}px)`, opacity: prog,
                }}>{w.t}</span>
              );
            })}
          </div>
        ))}
      </div>
    </AbsoluteFill>
  );
};

const Teaser: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const dots = ["", ".", "..", "..."][Math.floor((frame / fps * 1.5) % 4)];
  return (
    <AbsoluteFill style={{ padding: PAD, alignItems: "flex-start", justifyContent: "flex-end" }}>
      <span style={{ fontFamily, fontWeight: 800, fontSize: SMALL_FS, color: YELLOW, textShadow: SH }}>
        आगे देखिए{dots}
      </span>
    </AbsoluteFill>
  );
};

const WatchTip: React.FC = () => (
  <AbsoluteFill style={{ padding: PAD, alignItems: "flex-end", justifyContent: "flex-end" }}>
    <div style={{ fontFamily, fontWeight: 800, fontSize: SMALL_FS, color: YELLOW, textShadow: SH }}>Watch at 1.2x</div>
    <div style={{ fontFamily, fontWeight: 700, fontSize: SMALL_FS * 0.7, color: WHITE, textShadow: SH }}>for best experience</div>
  </AbsoluteFill>
);

// 🔥 The <Sequence from={S(...)} /> durationInFrames={S(...)} values below MUST stay
// NUMERIC LITERALS — build_overlay_windows.py AND cta_segs.py both regex-scrape them.
export const KhadeHanumanUjjainOverlays: React.FC = () => {
  const { fps } = useVideoConfig();
  const S = (s: number) => Math.round(s * fps);
  return (
    <AbsoluteFill>
      <HolesLayer holes={HOLES_DATA_URI} width={1280} height={720}>
        {KHADE_HANUMAN_PHRASES.map((p, i) => (
          <Sequence key={i} from={S(p.start)} durationInFrames={S(p.out - p.start)}>
            <PhraseCard p={p} />
          </Sequence>
        ))}
      </HolesLayer>

      <Sequence from={S(3)} durationInFrames={S(87)}>
        <Teaser />
      </Sequence>
      <Sequence from={S(0)} durationInFrames={S(5)}>
        <WatchTip />
      </Sequence>

      {/* CTA1 — लाइक 274.02 · हाइप 274.44 (कमेंट 284.14 has no tap) */}
      <Sequence from={S(272.36)} durationInFrames={S(11.5)}>
        <DevaKathaHypeCta screenSrc={CTA_SEG(1)} title={CTA_TITLE}
          commentHandle={CTA_HANDLE} commentText={CTA_COMMENT_TEXT}
          beats={ctaBeatsFromWords({ like: 274.02, hype: 274.44 }, 272.36)} />
      </Sequence>
      {/* CTA2 — "चैनल को सब्सक्राइब कर लीजिए" सब्सक्राइब 1182.83 */}
      <Sequence from={S(1181.17)} durationInFrames={S(11.5)}>
        <DevaKathaHypeCta screenSrc={CTA_SEG(2)} title={CTA_TITLE}
          commentHandle={CTA_HANDLE} commentText={CTA_COMMENT_TEXT}
          beats={ctaBeatsFromWords({ sub: 1182.83 }, 1181.17)} />
      </Sequence>
      {/* CTA3 — the SHARE ask पहुँचाइए 1214.99, 32 s after सब्सक्राइब so it gets its own phone.
          Ends 1224.83, seg cut ends 1225.23; narration runs to 1239.70 and the real clips follow. */}
      <Sequence from={S(1213.33)} durationInFrames={S(11.5)}>
        <DevaKathaHypeCta screenSrc={CTA_SEG(3)} title={CTA_TITLE}
          commentHandle={CTA_HANDLE} commentText={CTA_COMMENT_TEXT}
          beats={ctaBeatsFromWords({ share: 1214.99 }, 1213.33)} />
      </Sequence>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
